
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Hash, 
  Check, 
  CircleDollarSign, 
  Sparkles, 
  ArrowRight,
  ShieldCheck,
  Zap,
  LayoutGrid,
  Users,
  BarChart3,
  Globe,
  Library,
  X,
  Store,
  Languages,
  Sun,
  Moon,
  Info,
  UserCheck
} from 'lucide-react';
import { useTranslation } from '../lib/translations';

const Pricing: React.FC = () => {
  const { t, language, setLanguage } = useTranslation();
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const location = useLocation();

  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  const features = [
    { name: t.pricing.features.googleCalendar, free: true, pro: true },
    { name: t.pricing.features.googleDrive, free: true, pro: true },
    { name: t.pricing.features.creditSystem, free: true, pro: true },
    { name: t.pricing.features.homework, free: true, pro: true },
    { name: t.pricing.features.curriculum, free: true, pro: true },
    { name: t.pricing.features.studentPortal, free: true, pro: true },
    { name: t.pricing.features.meet, free: true, pro: true },
    { name: t.pricing.features.multiDevice, free: true, pro: true },
    { name: t.pricing.features.analytics, free: false, pro: true },
    { name: t.pricing.features.studentLimit, free: true, pro: false },
    { name: t.pricing.features.unlimitedStudents, free: false, pro: true },
  ];

  const gridFeatures = [
    { icon: LayoutGrid, title: t.pricing.grid.hub.title, desc: t.pricing.grid.hub.desc },
    { icon: Users, title: t.pricing.grid.roster.title, desc: t.pricing.grid.roster.desc },
    { icon: Zap, title: t.pricing.grid.automation.title, desc: t.pricing.grid.automation.desc },
    { icon: Library, title: t.pricing.grid.curriculum.title, desc: t.pricing.grid.curriculum.desc },
    { icon: CircleDollarSign, title: t.pricing.grid.financial.title, desc: t.pricing.grid.financial.desc },
    { icon: BarChart3, title: t.pricing.grid.growth.title, desc: t.pricing.grid.growth.desc },
    { icon: ShieldCheck, title: t.pricing.grid.privacy.title, desc: t.pricing.grid.privacy.desc },
    { icon: ArrowRight, title: t.pricing.grid.more.title, desc: t.pricing.grid.more.desc }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300">
      {/* Floating Navigation Header - Matches Landing Page */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
            <Hash size={18} className="text-white" />
          </div>
          <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
        </Link>
        
        <div className="flex items-center gap-4 md:gap-8">
          <Link 
            to="/teachers" 
            className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <Store size={16} />
            {t.nav.marketplace}
          </Link>
          
          <Link 
            to="/pricing" 
            className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <CircleDollarSign size={16} />
            {t.nav.pricing}
          </Link>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"
            >
              <Languages size={16} />
              {language.toUpperCase()}
            </button>
            <button 
              onClick={() => setIsDark(!isDark)}
              className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link 
              to="/login" 
              className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl"
            >
              {t.landing.enterApp}
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-40 pb-20 px-6">
        <div className="max-w-5xl mx-auto space-y-16">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm mb-2">
              <CircleDollarSign size={12} /> {t.nav.pricing}
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">
              {t.pricing.title}
            </h1>
            <p className="text-slate-500 font-medium text-lg max-w-2xl mx-auto">
              {t.pricing.subtitle}
            </p>
          </div>

          {/* Student Specific Box */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 shadow-xl overflow-hidden relative group">
            <div className="absolute top-0 right-0 p-12 opacity-5 group-hover:opacity-10 transition-opacity">
               <UserCheck size={120} />
            </div>
            <div className="relative z-10 flex flex-col md:flex-row gap-12">
               <div className="md:w-1/3">
                  <div className="inline-flex p-3 bg-teal-50 dark:bg-teal-900/20 text-teal-600 rounded-2xl mb-6">
                    <Users size={32} />
                  </div>
                  <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">{t.pricing.forStudents}</h3>
                  <p className="text-teal-600 font-black text-lg uppercase tracking-widest">{t.pricing.studentPricingTitle}</p>
               </div>
               <div className="md:w-2/3 space-y-6">
                  <p className="text-slate-500 font-medium leading-relaxed italic text-lg">
                    {t.pricing.studentPricingDesc}
                  </p>
                  <div className="grid sm:grid-cols-2 gap-4">
                     {t.pricing.studentPricingPoints.map((p: string, i: number) => (
                       <div key={i} className="flex items-center gap-3 text-sm font-bold text-slate-700 dark:text-slate-300">
                         <div className="p-1 bg-teal-100 dark:bg-teal-500/10 rounded-full">
                           <Check size={14} className="text-teal-600" />
                         </div>
                         {p}
                       </div>
                     ))}
                  </div>
                  <div className="pt-4">
                    <Link to="/teachers" className="text-sm font-black uppercase tracking-widest text-teal-600 hover:text-teal-700 flex items-center gap-2 transition-all">
                      {t.landing.ctaMarketplace} <ArrowRight size={16} />
                    </Link>
                  </div>
               </div>
            </div>
          </div>

          <div className="h-px bg-slate-200 dark:bg-white/5"></div>

          {/* Teacher Pricing Section */}
          <div className="space-y-12">
            <div className="text-center">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-widest">{t.pricing.forTeachers}</h2>
            </div>

            {/* Test Period Information Box */}
            <div className="bg-indigo-600 text-white rounded-2xl p-8 relative overflow-hidden shadow-2xl shadow-indigo-900/20">
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
                <div className="shrink-0 w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20">
                  <Sparkles size={32} className="text-white animate-pulse" />
                </div>
                <div className="text-center md:text-left space-y-2">
                  <h3 className="text-xl font-black uppercase tracking-widest">{t.pricing.testPeriodTitle}</h3>
                  <p className="text-indigo-100 text-sm font-medium leading-relaxed">
                    {t.pricing.testPeriodDesc}
                  </p>
                </div>
              </div>
              <div className="absolute top-0 right-0 p-12 opacity-10">
                <Globe size={120} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Free Tier */}
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-10 flex flex-col shadow-sm transition-all hover:border-slate-300 dark:hover:border-white/10">
                <div className="mb-10">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-2">{t.pricing.freeTier}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter">€0</span>
                    <span className="text-slate-400 text-sm font-bold">{t.pricing.perMonth}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-12 flex-1">
                  {features.filter(f => f.free).map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm font-bold text-slate-600 dark:text-slate-300">
                      <div className="shrink-0 p-0.5 bg-teal-100 dark:bg-teal-500/10 rounded-full">
                        <Check size={14} className="text-teal-600" />
                      </div>
                      {f.name}
                    </div>
                  ))}
                  {features.filter(f => !f.free).map((f, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm font-bold text-slate-300 dark:text-slate-600 line-through">
                      <div className="shrink-0 p-0.5 bg-slate-50 dark:bg-slate-800 rounded-full">
                        <X size={14} className="text-slate-300 dark:text-slate-600" />
                      </div>
                      {f.name}
                    </div>
                  ))}
                </div>

                <Link 
                  to="/login" 
                  className="w-full py-4 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white font-black text-[11px] uppercase tracking-widest text-center hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
                >
                  {t.pricing.getStarted}
                </Link>
              </div>

              {/* Pro Tier */}
              <div className="bg-white dark:bg-slate-900 border-2 border-purple-600 rounded-3xl p-10 flex flex-col shadow-2xl relative">
                <div className="absolute top-0 right-10 -translate-y-1/2 bg-purple-600 text-white px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg">
                  {t.pricing.recommended}
                </div>

                <div className="mb-10">
                  <h3 className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-600 mb-2">{t.pricing.proTier}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter">€4.99</span>
                    <span className="text-slate-400 text-sm font-bold">{t.pricing.perMonth}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-12 flex-1">
                  {features.map((f, i) => (
                    <div key={i} className={`flex items-center gap-3 text-sm font-bold ${f.pro ? 'text-slate-900 dark:text-white' : 'text-slate-300 line-through opacity-50'}`}>
                      <div className={`shrink-0 p-0.5 ${f.pro ? 'bg-purple-100 dark:bg-purple-500/10' : 'bg-slate-50 dark:bg-slate-800'} rounded-full`}>
                        <Check size={14} className={f.pro ? 'text-purple-600' : 'text-slate-300'} />
                      </div>
                      {f.name}
                    </div>
                  ))}
                </div>

                <Link 
                  to="/login" 
                  className="w-full py-4 rounded-xl bg-purple-600 text-white font-black text-[11px] uppercase tracking-widest text-center shadow-xl shadow-purple-500/30 hover:bg-purple-700 hover:scale-[1.02] transition-all"
                >
                  {t.pricing.upgradeToPro}
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-500/20 p-6 rounded-2xl flex gap-4 items-start shadow-inner">
             <Info size={24} className="text-amber-600 shrink-0 mt-1" />
             <div>
                <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-1">{t.pricing.directPaymentsTitle}</h4>
                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                  {t.pricing.directPaymentsDesc}
                </p>
             </div>
          </div>

          {/* Detailed Feature List */}
          <div className="pt-24 space-y-16">
            <div className="text-center">
              <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{t.pricing.footerTitle}</h2>
              <p className="text-slate-500 font-medium mt-2">{t.pricing.footerSubtitle}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
               {gridFeatures.map((feat, i) => (
                 <div key={i} className="space-y-3">
                    <div className="w-10 h-10 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-xl flex items-center justify-center text-purple-600 shadow-sm">
                      <feat.icon size={20} />
                    </div>
                    <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">{feat.title}</h4>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">{feat.desc}</p>
                 </div>
               ))}
            </div>
          </div>
        </div>
      </main>
      
      <footer className="py-20 px-6 text-center border-t border-slate-100 dark:border-white/5">
         <div className="max-w-4xl mx-auto flex flex-col items-center gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="p-2.5 bg-slate-100 dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-white/5 group-hover:scale-110 transition-transform">
                <Hash size={20} className="text-purple-600" />
              </div>
              <span className="font-black text-slate-400 dark:text-slate-500 text-xs uppercase tracking-[0.3em] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">SharpNote &copy; 2026</span>
            </Link>
            <div className="flex items-center gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
               <Link to="/privacy" className="hover:text-purple-600 transition-colors">{t.landing.privacy}</Link>
               <Link to="/terms" className="hover:text-purple-600 transition-colors">{t.landing.terms}</Link>
               <Link to="/support" className="hover:text-purple-600 transition-colors">{t.landing.footerSupport}</Link>
            </div>
         </div>
      </footer>
    </div>
  );
};

export default Pricing;
